import { useState } from "react";
import { Home, LayoutGrid, Tag, LogIn, ArrowRight, Lock } from "lucide-react";
import AdvancedExpertDialog from "@/components/public/AdvancedExpertDialog";
import { useLanguageSwitch } from "@/hooks/useLanguageSwitch";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, useSidebar } from "@/components/ui/sidebar";
import MarkerHighlight from "@/components/MarkerHighlight";

export const GO_TRIAL_EVENT = "rl:go-trial";
export const GO_CLUB_EVENT = "rl:go-club";

/** Menú lateral reducido de Home 2 (/go): solo anclas de la propia landing. */
export default function GoSidebar({ lang, onLogin, isAuthenticated, onPanel }: {
  lang: "es" | "en";
  onLogin: () => void;
  isAuthenticated: boolean;
  onPanel: () => void;
}) {
  const es = lang === "es";
  const { isMobile, setOpenMobile, state } = useSidebar();
  const mini = state === "collapsed" && !isMobile;
  const [expertOpen, setExpertOpen] = useState(false);
  const { switchLang } = useLanguageSwitch();
  const close = () => { if (isMobile) setOpenMobile(false); };
  const onHome = () => /^\/(en\/?)?$/.test(window.location.pathname);
  const fire = (ev: string) => { close(); if (onHome()) window.dispatchEvent(new Event(ev)); else window.location.assign(`${es ? "/" : "/en"}#planes`); };
  const goTo = (id: string) => {
    close();
    if (!onHome()) {
      window.location.assign(`${es ? "/" : "/en"}${id === "top" ? "" : `#${id}`}`);
      return;
    }
    if (id === "top") window.scrollTo({ top: 0, behavior: "smooth" });
    else document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const items = [
    { id: "top", label: es ? "Inicio" : "Home", Icon: Home },
    { id: "que-incluye", label: es ? "Herramientas" : "Tools", Icon: LayoutGrid },
    { id: "planes", label: es ? "Planes" : "Plans", Icon: Tag },
  ];

  return (
    <Sidebar collapsible="icon" className="border-r border-border">
      <SidebarHeader className={mini ? "items-center px-0 pt-5" : "px-5 pt-6"}>
        {mini ? (
          <a href={es ? "/" : "/en"} aria-label="Retail Lemon" className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary font-display text-base font-extrabold tracking-tight text-primary-foreground">RL</a>
        ) : <a href={es ? "/" : "/en"} className="block font-display text-2xl font-bold leading-none tracking-tight">
          RETAIL<MarkerHighlight height="0.78em" className="px-[0.06em] text-foreground">LEMON</MarkerHighlight>
        </a>}
      </SidebarHeader>
      <SidebarContent className={mini ? "items-center px-0 pt-4" : "px-3 pt-4"}>
        <nav className="flex flex-col gap-1">
          {items.map(({ id, label, Icon }) => (
            <button key={id} type="button" onClick={() => goTo(id)} title={mini ? label : undefined} aria-label={label} className={mini ? "flex h-11 w-11 items-center justify-center rounded-xl transition-colors hover:bg-muted" : "flex min-h-[44px] items-center gap-3 rounded-xl px-3 text-left text-sm font-semibold transition-colors hover:bg-muted"}>
              <Icon className="h-4 w-4" strokeWidth={1.5} />{!mini && label}
            </button>
          ))}
        </nav>
      </SidebarContent>
      <SidebarFooter className={mini ? "items-center gap-2 px-0 pb-5" : "gap-2 px-4 pb-6"}>
        {mini ? (
          <>
            <button type="button" title={isAuthenticated ? (es ? "Ir al Panel" : "Go to Panel") : (es ? "Acceso anticipado" : "Early access")} aria-label={isAuthenticated ? (es ? "Ir al Panel" : "Go to Panel") : (es ? "Acceso anticipado" : "Early access")} onClick={() => (isAuthenticated ? onPanel() : fire(GO_TRIAL_EVENT))} className="flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground hover:brightness-95"><ArrowRight className="h-4 w-4" strokeWidth={1.5} /></button>
            {!isAuthenticated && <button type="button" title={es ? "Pregunta a un experto" : "Ask an expert"} aria-label={es ? "Pregunta a un experto" : "Ask an expert"} onClick={() => setExpertOpen(true)} className="flex h-11 w-11 items-center justify-center rounded-xl border border-border hover:bg-secondary"><Lock className="h-4 w-4" strokeWidth={1.5} /></button>}
            {!isAuthenticated && <button type="button" title={es ? "Iniciar sesión" : "Log in"} aria-label={es ? "Iniciar sesión" : "Log in"} onClick={onLogin} className="flex h-11 w-11 items-center justify-center rounded-xl text-muted-foreground hover:bg-muted hover:text-foreground"><LogIn className="h-4 w-4" strokeWidth={1.5} /></button>}
            <button type="button" onClick={() => switchLang(es ? "en" : "es")} aria-label={es ? "English" : "Español"} className="flex h-11 w-11 items-center justify-center rounded-full border border-border text-xs font-semibold hover:bg-secondary">{es ? "EN" : "ES"}</button>
            <AdvancedExpertDialog open={expertOpen} onOpenChange={setExpertOpen} lang={es ? "es" : "en"} />
          </>
        ) : isAuthenticated ? (
          <button type="button" onClick={() => { close(); onPanel(); }} className="flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-primary text-sm font-bold text-primary-foreground hover:bg-primary/90">
            {es ? "Ir al Panel" : "Go to Panel"}<ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <>
            <button type="button" onClick={() => fire(GO_TRIAL_EVENT)} className="flex min-h-11 items-center justify-center rounded-full bg-primary px-4 text-sm font-bold text-primary-foreground hover:brightness-95">
              {es ? "Acceso anticipado" : "Early access"}
            </button>
            <button type="button" onClick={() => fire(GO_CLUB_EVENT)} className="flex min-h-11 items-center justify-center gap-2 rounded-full border-2 border-primary bg-card px-4 text-sm font-bold text-foreground hover:bg-secondary">
              <span className="whitespace-nowrap">{es ? "Únete al Club Pro" : "Join Club Pro"}</span>
            </button>
            <button type="button" onClick={() => setExpertOpen(true)} className="flex min-h-11 items-center justify-center gap-2 rounded-xl border border-border bg-card px-3 text-sm font-semibold text-foreground hover:bg-secondary">
              <Lock className="h-4 w-4 shrink-0" strokeWidth={1.5} />{es ? "Pregunta a un experto" : "Ask an expert"}
            </button>
            <AdvancedExpertDialog open={expertOpen} onOpenChange={setExpertOpen} lang={es ? "es" : "en"} />
            <div className="flex min-h-11 items-center justify-between gap-2 pt-1">
              <button type="button" onClick={() => { close(); onLogin(); }} className="flex min-h-11 items-center gap-2 rounded-lg pr-2 text-sm font-semibold text-muted-foreground hover:text-foreground">
                <LogIn className="h-4 w-4" strokeWidth={1.5} />{es ? "Iniciar sesión" : "Log in"}
              </button>
              <div className="flex gap-2">
                {(["es", "en"] as const).map((l) => (
                  <button key={l} type="button" aria-pressed={lang === l} onClick={() => switchLang(l)} className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${lang === l ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-secondary"}`}>{l.toUpperCase()}</button>
                ))}
              </div>
            </div>
          </>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
